import { FC, useCallback, useContext, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { formatDate, splitDateTime } from "capitalrx-utilities";
import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { getFormattedValue } from "capitalrx-components-2.0/lib/general/Table/helpers";

import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { Logo, PrintableWrapper, ThemedButton } from "@/App/components";
import { getCustomerSupportNumber } from "@/App/utils/utils";
import { ExperienceContext } from "@/App/context";
import { MedicalClaimListRequest, useListMedicalClaimsQuery } from "@/App/api/claims";

import "./PrintClaims.scss";

type MutableClaimListRequest = {
  -readonly [K in keyof MedicalClaimListRequest]: MedicalClaimListRequest[K];
};

export const PrintMedicalClaimsPage: FC = () => {
  const { i18n, t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { customerSupportPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = getCustomerSupportNumber(memberEligibility, customerSupportPhoneNumber);
  const { first_name, last_name, external_member_id, birth_date } = memberEligibility.member;
  const [searchParams] = useSearchParams();
  const language: string = i18n.language ?? "en";
  const now = DateTime.now().setLocale(language);
  const formattedDate = now.toLocaleString(DateTime.DATE_FULL);
  const ytd = searchParams.get("ytd");
  const month = searchParams.get("month");
  const year = searchParams.get("year");
  const status = searchParams.get("status");
  const network = searchParams.get("network");
  const personCode = searchParams.get("person_code");
  const medicarePrescriptionPaymentPlanIndicator = searchParams.get("medicare_prescription_payment_plan_indicator");
  const printRef = useRef();

  const listClaimsFilters: MedicalClaimListRequest = useMemo(() => {
    // we intend to grab all relevant claims since this will be printed and there are no paging controls
    const filters: MutableClaimListRequest = {
      results_per_page: 1000,
      page_number: 1,
    };
    if (ytd) {
      filters.year_to_date = true;
    } else if (!month && year) {
      filters.year = +year;
    } else {
      filters.month = +month;
      filters.year = +year;
    }

    if (personCode) {
      filters.person_code = personCode;
    }

    if(status) {
      filters.status = [status] as MedicalClaimListRequest["status"];
    }

    if(network) {
      filters.network = network as MedicalClaimListRequest["network"];
    }



    return { ...filters };
  }, []);

  const { data: claims, isLoading: isLoadingClaims } = useListMedicalClaimsQuery(listClaimsFilters);

  const statusMap = {
    pended: translate("CLAIMS.statusOptions.inProgress"),
    partially_paid: translate("CLAIMS.statusOptions.partiallyApproved"),
    paid: translate("CLAIMS.statusOptions.approved"),
    denied: translate("CLAIMS.statusOptions.denied"),
  };

  const networkMap = {
    in_network_tier_1: translate("CLAIMS.networkOptions.inNetwork"),
    out_of_network: translate("CLAIMS.networkOptions.outOfNetwork"),
  };

  const tableRows = useMemo(() => {
    return (claims?.results || []).map(({ medical_claim }, index: number) => ({
      id: medical_claim.id,
      key: index,
      date_of_service: medical_claim.minimum_service_date,
      status: medical_claim.status,
      provider: medical_claim.billing_provider_last_name_or_organizational_name,
      network: medical_claim.network,
      received: medical_claim.submission_date,
      patient_paid: medical_claim.total_patient_pay_amount,
    }));
  }, [claims]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const onPrint = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.printMedicalClaimsHistory);
    handlePrint();
  }, [handlePrint]);

  const pageDescription = useMemo(() => {
    let generateDate: DateTime;
    let when: string;
    if (ytd) {
      generateDate = now;
      when = `${translate("PRINT_CLAIMS_PAGE.currentYearToDate")} - ${generateDate.year}`;
    } else if (!month && year) {
      generateDate = DateTime.fromObject({
        year: +year,
        month: 1,
      });
      when = `${generateDate.year}`;
    } else {
      generateDate = DateTime.fromObject({
        year: +year,
        month: +month,
      });
      when = `${translate(`PRINT_CLAIMS_PAGE.${generateDate.monthLong.toLowerCase()}`)} ${generateDate.year}`;
    }

    return (
      <Body>
        {translate("PRINT_CLAIMS_PAGE.allClaimsFor")} <Semibold>{when}</Semibold>
      </Body>
    );
  }, [medicarePrescriptionPaymentPlanIndicator, month, ytd]);

  return (
    <LoadingGroup loading={isLoadingClaims}>
      <PrintableWrapper ref={printRef}>
        <Stack style={{ height: "100%" }}>
          <Shelf alignment="top">
            <Logo />
            <Shelf.Spacer />
            <ThemedButton
              testId="print-medical-claims-history"
              className="print-button"
              onClick={onPrint}
              appearance="text"
              icon="print"
            />
          </Shelf>
          <div>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.memberName")}</Semibold> {first_name} {last_name}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.dateOfBirth")}</Semibold> {formatDate(birth_date, "MM/DD/YYYY")}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.memberId")}</Semibold> {external_member_id}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.personCode")}</Semibold> {personCode}
            </Body>
          </div>
          <Shelf>
            <Body size="medium">
              <Semibold>{translate("PRINT_CLAIMS_PAGE.medicalClaimsSummary")}</Semibold>
            </Body>
            <Shelf.Spacer />
            <Body>{`${translate("PRINT_CLAIMS_PAGE.prepared")} ${formattedDate}`}</Body>
          </Shelf>
          {pageDescription}
          <LoadingGroup.Placeholder
            fallback={
              <Shelf alignment="center">
                <Stack alignment="center">
                  <Spinner size="large" />
                </Stack>
              </Shelf>
            }
          >
            <table className="claims-history-table">
              <thead>
                <tr>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.dateOfService")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.claimId")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.status")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.provider")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.network")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.received")}</Semibold></Body>
                  </th>
                  <th>
                    <Body textAlign="right"><Semibold>{translate("PRINT_CLAIMS_PAGE.patientPay")}</Semibold></Body>
                  </th>
                </tr>
              </thead>
              <tbody data-testid="table-claims">
                {tableRows.map((claim) => {
                  return (
                    <tr data-testid={`medical-claim-${claim.key}-row`} key={claim.key}>
                      <td data-testid={`medical-claim-${claim.key}-date-of-service`}><Body>{isLoadingClaims ? "" : splitDateTime(claim.date_of_service)}</Body></td>
                      <td data-testid={`medical-claim-${claim.key}-id`}><Body>#{String(claim.id).padStart(3, "0")}</Body></td>
                      <td data-testid={`medical-claim-${claim.key}-status`}><Body>{statusMap[claim.status]}</Body></td>
                      <td data-testid={`medical-claim-${claim.key}-provider`}><Body>{claim.provider}</Body></td>
                      <td data-testid={`medical-claim-${claim.key}-network`}><Body>{networkMap[claim.network]}</Body></td>
                      <td data-testid={`medical-claim-${claim.key}-received`}><Body>{isLoadingClaims ? "" : splitDateTime(claim.received)}</Body></td>
                      <td data-testid={`medical-claim-${claim.key}-patient-paid`}><Body textAlign="right">{claim.status !== "pended" ? `$${getFormattedValue(claim.patient_paid || 0)}` : "—"}</Body></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </LoadingGroup.Placeholder>
          <Stack.Spacer />
          <Stack spacing="small">
            <Body size="medium"><Semibold>{translate("PRINT_CLAIMS_PAGE.customerSupport")}</Semibold></Body>
            <Body testId="customer-support-number">
              {translate("PRINT_CLAIMS_PAGE.contactCustomerCareAt")} <Semibold>{customerSupportNumber}</Semibold>.
            </Body>
          </Stack>
        </Stack>
      </PrintableWrapper>
    </LoadingGroup>
  );
};
